import { appStore } from "../../Orchestrator/Recoils/store";
import {
    shuttleMinPriceCacheAtom,
    shuttleStopAvailabilityAtom,
} from "../../Orchestrator/Recoils/EndToEndJourneyAtom";
import { isInfantPassenger } from "../../Lib/Utils/validationUtil";
import { withoutDepartedTrips } from "../../Lib/Utils/shuttleTrips";
import { ILegTrain, IPassenger } from "../../Orchestrator/Types/Index";
import { RideHailingMode } from "../../Orchestrator/Types/Interface/IRideHailing";
import { IShuttleStopOption, IShuttleTrip } from "../../Orchestrator/Types/Interface/IShuttleBus";
import {
    fetchShuttleOffersForStop,
    shuttleDepartureDate,
    shuttleStationRef,
} from "./shuttleOffersController";

/**
 * Session cache for shuttle offers, so the same `POST /offers` is not sent
 * three times for one selection.
 *
 * Before this, adding a shuttle one way cost 5+ offers calls: the modal swept
 * every stop on open (N), the stop click refetched the stop it had just swept
 * (byte-identical whenever no passenger was checked yet, because both send the
 * full passenger set), Confirm refetched again, and closing + reopening the
 * modal repeated the whole sweep because nothing consulted what it had already
 * fetched. On a round trip that ran per leg per side.
 *
 * `POST /offers` is a pure search — the response carries no hold, no expiry and
 * no reservation (Turnit `OfferCollectionResponse`), so caching a result
 * reserves nothing and skipping a call releases nothing. What it does carry is
 * a price and a set of departures, both of which go stale, hence the TTL.
 *
 * SAFETY, and this changed: Confirm used to re-fetch through
 * `fetchShuttleOffersForStop` before booking, which made this cache
 * display-only. That re-fetch has been removed, so the offerId booked is
 * whichever one THIS cache last minted, and the TTL is now the only thing
 * bounding how stale a purchase can be. Two consequences to keep in mind before
 * lengthening it:
 *  - there is no availability field on IShuttleTrip, so nothing now checks a
 *    departure is still sellable until POST /booked-offers rejects it;
 *  - offers expire 30min after they are minted, independently of TTL_MS.
 * The booking payload does still send passengerRefs alongside offerId, so the
 * backend is told who is travelling regardless.
 *
 * The passenger specs are in the cache key so a list priced for three riders is
 * never DISPLAYED as one priced for one.
 */

/** Matches the shape `fetchShuttleOffersForStop` sends as offers passengers. */
export interface ShuttlePassengerSpec {
    externalRef: string;
    age?: number;
    cards?: any[];
    prmNeeds?: string[];
}

export interface ShuttleTripsQuery {
    /**
     * REQUIRED, and the reason is a money bug — see shuttleTripsKey. Typed
     * non-optional so a new call site cannot quietly omit it.
     */
    bookingId: string;
    stop: IShuttleStopOption;
    side: RideHailingMode;
    trainLeg?: ILegTrain;
    passengers: ShuttlePassengerSpec[];
}

/**
 * Prices and departures both drift, and a shuttle can sell out. Five minutes
 * matches the TTL pinned on `/stops` for the same reason (d879cd7d): long
 * enough that one booking session is a single fetch, short enough that a
 * backend change reaches the user the same day.
 */
const TRIPS_TTL_MS = 5 * 60 * 1000;

interface CacheEntry {
    at: number;
    trips: IShuttleTrip[];
}

/**
 * How long a FAILED request is remembered. Short, because a failure should be
 * retryable — but not zero, because nothing else stops a retry storm: a failure
 * is not a cacheable result, so without this every effect re-run (the card's
 * preload, then the modal's sweep on open) re-fires the same doomed call. That
 * is the pile of red `offers` rows in the network panel on a station whose
 * shuttle stop 4xx's.
 */
const FAILURE_TTL_MS = 30 * 1000;

const settled = new Map<string, CacheEntry>();
const failed = new Map<string, number>();
const inflight = new Map<string, Promise<IShuttleTrip[] | null>>();

/**
 * The request identity, as a string.
 *
 * Every field the offers request varies on is in here, and each is derived by
 * the SAME function the request itself uses (`shuttleStationRef`,
 * `shuttleDepartureDate`) — a key that recomputed either independently could
 * drift from what was actually sent and serve one station's or one day's times
 * under another's key.
 *
 * The passenger specs are included whole, not just their refs: a checkout
 * session can edit a rider's age, cards or PRM needs after the first fetch, and
 * all three change the price the backend returns.
 *
 * The BOOKING ID is in the key for the same reason useHydrateNolOffers keys its
 * fetch on the booking: this cache is module-level, so it survives SPA
 * navigation from one booking to the next, and moving from booking A to B must
 * refetch rather than reuse A's offers.
 *
 * Without it the key collided across bookings and the collision was total.
 * GetSearchResultAction mints passenger refs into a singleton's
 * `storedPassengerRefs`, keyed `ADULT_0`-style, and REUSES them for every later
 * search in the page session — so rebooking the same route, date and party
 * produced a byte-identical key. Booking B was then served booking A's cached
 * trips, and because Confirm no longer re-prices (see the SAFETY note above)
 * the offerId it POSTed was the one booking A had already consumed. Reported
 * from INT: the first shuttle add works, every later booking's add fails until
 * site data is cleared — clearing reloads the page, which is what actually
 * dropped this Map. Self-healing after TRIPS_TTL_MS, which is why it looked
 * intermittent.
 */
export function shuttleTripsKey(query: ShuttleTripsQuery): string {
    const { bookingId, stop, side, trainLeg, passengers } = query;
    const pax = [...(passengers || [])]
        .sort((a, b) => (a.externalRef || "").localeCompare(b.externalRef || ""))
        .map((p) => [p.externalRef, p.age ?? null, p.cards || [], p.prmNeeds || []]);
    return JSON.stringify([
        bookingId,
        side,
        shuttleStationRef(side, trainLeg),
        stop?.stopId || "",
        shuttleDepartureDate(side, trainLeg),
        pax,
    ]);
}

function readFresh(key: string): IShuttleTrip[] | null {
    const hit = settled.get(key);
    if (!hit) return null;
    if (Date.now() - hit.at > TRIPS_TTL_MS) {
        settled.delete(key);
        return null;
    }
    // A new array, never the entry itself: it is handed to every caller and
    // lands in React state. Departed trips are dropped on READ rather than on
    // write, so a cached day's list stays correct as the clock moves.
    return withoutDepartedTrips(hit.trips);
}

/**
 * Synchronous cache read. Lets a caller render a cached list in the same commit
 * instead of flashing a spinner for a request it is not going to make.
 */
export function peekShuttleTrips(query: ShuttleTripsQuery): IShuttleTrip[] | null {
    return readFresh(shuttleTripsKey(query));
}

/**
 * Cached + de-duplicated `fetchShuttleOffersForStop`.
 *
 * Concurrent callers for the same key share one in-flight request — the modal's
 * sweep and a fast stop click would otherwise race and send two.
 */
export async function getShuttleTrips(query: ShuttleTripsQuery): Promise<IShuttleTrip[] | null> {
    const key = shuttleTripsKey(query);

    const cached = readFresh(key);
    if (cached) return cached;

    const pending = inflight.get(key);
    if (pending) return pending;

    // Recently failed: do not re-fire yet. Callers still get null and render
    // their error state, they just do not each buy another round trip for it.
    const failedAt = failed.get(key);
    if (failedAt !== undefined) {
        if (Date.now() - failedAt < FAILURE_TTL_MS) return null;
        failed.delete(key);
    }

    const run = fetchShuttleOffersForStop({
        stop: query.stop,
        mode: query.side,
        trainLeg: query.trainLeg,
        passengers: query.passengers,
        setTrips: () => {},
        setStatus: () => {},
    })
        .then((trips) => {
            // A result is cached for the full TTL; a failure only for
            // FAILURE_TTL_MS, so it retries soon but not on every re-render.
            // An empty array is a real answer ("no shuttles that day") and IS
            // cached as a result.
            if (trips) {
                settled.set(key, { at: Date.now(), trips });
                failed.delete(key);
            } else {
                failed.set(key, Date.now());
            }
            return trips && withoutDepartedTrips(trips);
        })
        // A rejection must stamp `failed` too, or the next render re-fires the
        // same doomed call — the storm FAILURE_TTL_MS exists to stop. It also
        // keeps the rejection from escaping into the `void warm...()` and
        // un-caught `.then()` call sites as an unhandled promise rejection, and
        // stops fetchTrips from leaving tripsStatus stuck on "loading".
        // Latent today (BaseAction swallows), which is exactly why it is cheap
        // to close now rather than after it starts throwing.
        .catch(() => {
            failed.set(key, Date.now());
            return null;
        })
        .finally(() => {
            inflight.delete(key);
        });

    inflight.set(key, run);
    return run;
}

/**
 * Drop everything. Test isolation only — the module-level cache outlives a
 * render, so a suite that does not clear it lets one test's fetch serve the
 * next test's assertions.
 *
 * DO NOT call this after a confirm or a refund. That was tried and reverted:
 * clearing on confirm meant reopening the shuttle you had just booked
 * re-swept every stop, because the card's preload effect does not re-run (none
 * of its deps changed) and the modal then met an empty cache. It also dropped
 * every OTHER side and leg over one side's purchase.
 *
 * Nothing rendered from this cache changes when an offer is booked or refunded:
 * IShuttleTrip carries departure times, duration and per-pax price — no seat
 * count, no availability (GetShuttleE2EOffersAction parses none and
 * ShuttleTripsPanel renders none). That, on its own, is the argument: a booking
 * does not alter what the list would say. The TTL is what bounds staleness here,
 * not eviction.
 *
 * (This used to also argue "and Confirm re-prices live before booking, so a
 * stale list can never produce a wrong purchase". That re-fetch is gone — see
 * ShuttleBusModal.handleConfirm — so it is no longer part of the case. A FAILED
 * confirm does clear this cache, deliberately: confirmShuttle's catch calls
 * invalidateShuttleTrips so a retry cannot re-send an offerId already known bad.)
 */
export function invalidateShuttleTrips(): void {
    settled.clear();
    failed.clear();
    inflight.clear();
}

/**
 * Fetch every stop's offers for one station-side and publish the min per-pax
 * price: per stop as the return value, and station-level into
 * `shuttleMinPriceCacheAtom` for the collapsed "from Đ N" row.
 *
 * This is the sweep that used to live inline in ShuttleBusModal. It is shared so
 * the surface owning a leg can preload on mount — OnwardTravelCard in checkout,
 * ManageEndToEndRow in booking details — and the modal then opens against a warm
 * cache instead of re-fetching what that surface already has.
 */
export async function warmShuttleStopPrices(args: {
    bookingId: string;
    side: RideHailingMode;
    stopOptions: IShuttleStopOption[];
    trainLeg?: ILegTrain;
    passengers: ShuttlePassengerSpec[];
}): Promise<Record<string, number>> {
    const { bookingId, side, stopOptions, trainLeg, passengers } = args;
    const stationRef = shuttleStationRef(side, trainLeg);
    const departureDate = shuttleDepartureDate(side, trainLeg);
    if (!stationRef || !(stopOptions || []).length || !(passengers || []).length) return {};

    const entries = await Promise.all(
        stopOptions.map(async (stop) => {
            const trips = await getShuttleTrips({ bookingId, stop, side, trainLeg, passengers });
            const prices = (trips || []).map((t) => t.perPaxPrice).filter((p) => p > 0);
            return {
                stopId: stop.stopId,
                // Did the backend answer at all? `min` is undefined for both
                // "answered, nothing priced" and "request failed", so the
                // min-price write below needs this to tell them apart.
                answered: trips !== null,
                // An EMPTY answer means "nothing to sell" — hide the stop.
                //
                // A FAILED request is only allowed to hide a stop that has
                // NEVER successfully answered. Hiding one that previously had
                // trips would let a single 5xx remove a sellable stop for the
                // rest of the session: the failure suppresses retry for
                // FAILURE_TTL_MS and the card's preload effect never re-runs
                // (none of its deps change), so nothing would bring it back.
                //
                // The first-time conflation is deliberate and is the case in
                // the Fujairah → Main Bus Station report: /offers 4xx's there
                // every time, and a row whose modal can only say "please try
                // again later" is worse than no row.
                hasTrips:
                    trips === null
                        ? appStore.get(shuttleStopAvailabilityAtom)[
                              stopAvailabilityKey(side, stationRef, stop.stopId, departureDate)
                          ] ?? false
                        : trips.length > 0,
                min: prices.length ? Math.min(...prices) : undefined,
            };
        })
    );

    // Availability is written for EVERY stop, including failed ones — those
    // carry forward their previous value via `hasTrips` above rather than
    // dropping to false.
    if (entries.length) {
        // Re-read at write time rather than closing over a pre-await value — the
        // other side's sweep landing in between would otherwise be lost.
        //
        // The per-stop read above (inside the map) IS a read-modify-write with a
        // gap. It is safe only because concurrent sweeps for the same key share
        // one in-flight promise and therefore one outcome, and because distinct
        // legs/sides produce distinct keys. If the dedup in getShuttleTrips ever
        // changes, revisit this.
        appStore.set(shuttleStopAvailabilityAtom, {
            ...appStore.get(shuttleStopAvailabilityAtom),
            ...Object.fromEntries(
                entries.map((e) => [
                    stopAvailabilityKey(side, stationRef, e.stopId, departureDate),
                    e.hasTrips,
                ])
            ),
        });
    }

    const found = entries
        .filter((e): e is typeof e & { min: number } => typeof e.min === "number")
        .map((e) => [e.stopId, e.min] as const);
    if (!found.length) {
        // Nothing priced — but WHY matters, and this is the same distinction
        // `hasTrips` makes above. If no stop even answered (a network blip on a
        // re-sweep), keep the last known price: dropping it leaves the row
        // advertising a shuttle with no price and there is no recovery path,
        // since the preload effect will not re-run and FAILURE_TTL_MS suppresses
        // retry. Only clear when the backend actually said "nothing here".
        if (!entries.some((e) => e.answered)) return {};
        const { [stationMinPriceKey(side, stationRef, departureDate)]: _dropped, ...rest } =
            appStore.get(shuttleMinPriceCacheAtom);
        appStore.set(shuttleMinPriceCacheAtom, rest);
        return {};
    }

    appStore.set(shuttleMinPriceCacheAtom, {
        ...appStore.get(shuttleMinPriceCacheAtom),
        [stationMinPriceKey(side, stationRef, departureDate)]: Math.min(
            ...found.map(([, v]) => v)
        ),
    });

    return Object.fromEntries(found);
}

/**
 * Key into shuttleMinPriceCacheAtom. Carries the date for the same reason
 * stopAvailabilityKey does: the atom is never reset and survives SPA
 * navigation, so a dateless key showed 12 Aug's "from Đ N" on a 13 Aug search
 * until the new sweep landed — and indefinitely if that sweep priced nothing.
 */
export function stationMinPriceKey(
    side: RideHailingMode,
    stationRef: string,
    departureDate: string
): string {
    return `${side}:${stationRef}:${departureDate}`;
}

/**
 * Key into shuttleStopAvailabilityAtom. One definition, so writers and readers
 * agree.
 *
 * The DATE is part of it. Shuttle service is per-day, the atom is never reset,
 * and it survives SPA navigation — so without the date, searching 12 Aug (no
 * departures) and then 13 Aug (departures) starts the second checkout with the
 * stop already filtered out. That mostly self-heals when the new sweep answers,
 * but if THAT sweep fails, `hasTrips` falls back to the stored value and reads
 * 12 Aug's `false`, hiding a sellable stop with no retry path. That is not the
 * accepted "never answered" case — the stop did answer, for a different day.
 */
export function stopAvailabilityKey(
    side: RideHailingMode,
    stationRef: string,
    stopId: string,
    departureDate: string
): string {
    return `${side}:${stationRef}:${stopId}:${departureDate}`;
}

/**
 * Drop stops the backend has confirmed have no departures.
 *
 * Unknown stops are KEPT: absence in the availability map means "not fetched
 * yet, or the fetch failed", and hiding a purchasable stop on a network blip
 * would silently cost a sale. So the picker starts complete and narrows once
 * the sweep answers — never the reverse.
 */
export function stopsWithTrips(
    stopOptions: IShuttleStopOption[],
    availability: Record<string, boolean> | undefined,
    side: RideHailingMode,
    // The leg, not a pre-computed station ref and date: both are derived here
    // with the same functions the WRITER uses, so a caller cannot key a read
    // differently from how the sweep keyed the write.
    trainLeg?: ILegTrain
): IShuttleStopOption[] {
    if (!availability) return stopOptions || [];
    const stationRef = shuttleStationRef(side, trainLeg);
    const departureDate = shuttleDepartureDate(side, trainLeg);
    return (stopOptions || []).filter(
        (stop) =>
            availability[stopAvailabilityKey(side, stationRef, stop.stopId, departureDate)] !== false
    );
}

/**
 * Infants share a guardian's place and ride free — excluded from the checklist,
 * from pricing and from the booking payload (the backend rejects them alone
 * anyway). Shared so the card and the modal can never disagree about who is
 * being priced, which would give them two different cache keys for one journey.
 */
export function shuttleOfferPassengers(passengers?: IPassenger[] | null): IPassenger[] {
    return (passengers || []).filter((p) => !isInfantPassenger(p));
}

/**
 * Offers requests must carry each passenger's real age/cards/prmNeeds. With
 * bare externalRefs the backend prices everyone as a default adult, silently
 * bypassing pax-type rules (e.g. an infant "sold alone") and type pricing.
 */
export function toShuttleOfferSpec(p: IPassenger): ShuttlePassengerSpec {
    return {
        externalRef: p.externalRef,
        age: p.age,
        cards: p.cards || [],
        prmNeeds: p.prmNeeds || [],
    };
}
